import { useNavigate, useParams } from 'react-router-dom';
import { fetchPloById, savePloDraft } from '../../../services/PloSixService';
import { useQueryClient } from '@tanstack/react-query';
import { useSettings } from '../../../context/SettingContext';
import { useEffect, useState } from 'react';


interface PLORowSixState {
  id: number;
  caseNo: string;              // ရာဇဝတ်ကြီးမှုအမှတ် (၂)
  plaintiff: string;           // တရားလို (၃)
  defendant: string;           // တရားခံ (၄)
  lawSection: string;          // တရားစွဲဆိုသည့် ဥပဒေ၊ ပုဒ်မ (၅)
  witnessName: string;         // နစ်နာသူ / မျက်မြင်သက်သေ (၆)
  officerName: string;         // တွေ့ဆုံဆွေးနွေးသည့် ဥပဒေအရာရှိအမည် (၇)
  interviewDate: string;       // တွေ့ဆုံသည့် နေ့ရက် (၈)
  remarks: string;             // မှတ်ချက် (၉)
}

interface PLOFormSixState {
  officeId: string;
  officeName: string;
  responsibleOfficerName:string;
  lawOffice: string;
  rows: PLORowSixState[];
}

export default function PLOFormSix() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>(); // Reads :id from route e.g. /plo-form-three/edit/:id

  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { settings } = useSettings();
  // const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];
  // const TOWNSHIP_OPTIONS = settings?.townships.map((t) => t.name) || [];

  const [formData, setFormData] = useState<PLOFormSixState>({
    officeId: '',
    officeName: '',
    responsibleOfficerName: '',
    lawOffice: '',
    rows: [
      {
        id: 1,
        caseNo: '',
        plaintiff: '',
        defendant: '',
        lawSection: '',
        witnessName: '',
        officerName: '',
        interviewDate: '',
        remarks: ''
      }
    ],
  });

  // Fetch record when editing existing ID
  useEffect(() => {
  if (!id) return;

  const loadSingleRecord = async () => {
    try {
      setIsLoadingRecord(true);
      const res = await fetchPloById(id);
      const data = res?.data || res;

      if (data) {
        const formDetails = data.ploFormThree || data;

        // 1. Get officeId (reads data.officeId directly from flattened object)
        const currentOfficeId = data.officeId || data.ploFormThree?.officeId || data.originOfficeId || '';

        // 2. Fixed log: log the extracted currentOfficeId directly
        console.log("this is full data:", data);
        console.log("this is office id:", currentOfficeId); // Logs: "cmso7yfpk000owoc3v33olrvp"

        // 3. Find matching office name in dropdown options
        const matchedOffice = settings?.offices?.find(
          (o) => o.id === currentOfficeId
        );

        setFormData({
          officeId: currentOfficeId,
          officeName: matchedOffice ? matchedOffice.name : '',
          responsibleOfficerName : data.responsibleOfficerName || data.ploFormThree?.responsibleOfficerName || '',
          lawOffice: data.lawOffice || data.ploFormSix?.lawOffice || '',
          rows: [
            {
              id: 1,
              caseNo: formDetails.caseNo || '',
              plaintiff: formDetails.plaintiff || '',
              defendant: formDetails.defendant || '',
              lawSection: formDetails.lawSection || '',
              witnessName: formDetails.witnessName || '',
              officerName: formDetails.officerName || '',
              interviewDate: formDetails.interviewDate || '',
              remarks: formDetails.remarks || ''
            }
          ]
        });
      }
    } catch (error) {
      console.error('Failed to load record for editing:', error);
      alert('အချက်အလက်များ ဆွဲယူ၍ မရရှိပါ။');
    } finally {
      setIsLoadingRecord(false);
    }
  };

  loadSingleRecord();
}, [id, settings?.offices]); // Include settings so officeName updates when settings load

  // const handleOfficeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  // const selectedName = e.target.value;

  //   // Find matching office object from settings
  //   const matchedOffice = settings?.offices.find(
  //     (o) => o.name.trim().toLowerCase() === selectedName.trim().toLowerCase()
  //   );

  //   setFormData((prev) => ({
  //     ...prev,
  //     officeName: selectedName,
  //     officeId: matchedOffice ? matchedOffice.id : '', // Attach ID if matched, else reset
  //   }));
  // };

  const handleRowChange = (rowId: number, field: keyof PLORowSixState, value: string) => {
    const updatedRows = formData.rows.map(row => {
      if (row.id === rowId) {
        return { ...row, [field]: value };
      }
      return row;
    });
    setFormData({ ...formData, rows: updatedRows });
  };

  const handleAddRow = () => {
    const newId = formData.rows.length > 0 ? Math.max(...formData.rows.map(r => r.id)) + 1 : 1;
    const newRow: PLORowSixState = {
        id: newId,
        caseNo: '',
        plaintiff: '',
        defendant: '',
        lawSection: '',
        witnessName: '',
        officerName: '',
        interviewDate: '',
        remarks: ''
    };
    setFormData({ ...formData, rows: [...formData.rows, newRow] });
  };

  const handleRemoveRow = (rowId: number) => {
    if (formData.rows.length === 1) {
      alert("အနည်းဆုံး အချက်အလက် Row တစ်ခု ရှိနေရပါမည်။");
      return;
    }
    setFormData({
      ...formData,
      rows: formData.rows.filter(row => row.id !== rowId)
    });
  };

  // Handle Save / Update Form
  const handleSaveForm = async () => {
    try {
      setIsSaving(true);

    const payload = formData.rows.map((row) => ({
      ...row,
      officeId: formData.officeId || undefined, // Sends officeId (or undefined/null if empty)
      responsibleOfficerName: formData.responsibleOfficerName || undefined,
      lawOffice: formData.lawOffice || undefined,
      id: id || undefined,
      status: 'DRAFT',
    }));

      // Pass `id` if editing existing record, or `null` if creating new
      await savePloDraft(id || null, payload);
      await queryClient.invalidateQueries({
        queryKey: ['ploFormSix'],
      });
      alert(id ? 'အချက်အလက်များ ပြင်ဆင်သိမ်းဆည်းပြီးပါပြီ။' : 'အချက်အလက်များကို အောင်မြင်စွာ သိမ်းဆည်းပြီးပါပြီ။');
      navigate(-1); // Go back to table view after saving
    } catch (error: any) {
      console.error('Failed to save PLO Form 3:', error);
      alert(error?.message || 'ဒေတာများ သိမ်းဆည်းရာတွင် အမှားအယွင်းရှိနေပါသည်။');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingRecord) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center font-sans text-sm text-slate-600">
        အချက်အလက်များ ပြန်လည်ဆွဲယူနေပါသည်...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4 flex flex-col items-center justify-start font-serif text-black">
      
      {/* Top Controller */}
      <div className="w-full max-w-[1400px] mb-4 flex justify-between items-center font-sans">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3 py-2 rounded shadow-sm transition"
          >
            ← နောက်သို့ (Back)
          </button>
          <h2 className="text-lg font-bold text-slate-800">
            {id ? 'ရှေ့ပြေးဥပဒေရုံးအမှုမှတ်တမ်း ပြင်ဆင်ရန် (Edit PLO Form 6)' : 'ရှေ့ပြေးဥပဒေရုံးအမှုမှတ်တမ်း (PLO Form 6)'}
          </h2>
        </div>

        <button
          type="button"
          onClick={() => alert("ပရင့်ထုတ်ရန် အစမ်းကြည့်ရှုခြင်း...")}
          className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-4 py-2 rounded shadow transition"
        >
          📄 Print Preview
        </button>
      </div>

      {/* Main Container */}
      <div className="w-full max-w-[1400px] bg-white shadow-xl border border-gray-300 p-8 space-y-6 overflow-x-auto">
        
        {/* Header Section */}
        {/* Header Title Section */}
        <div className="text-right text-xs font-bold font-sans text-gray-500">နောက်ဆက်တွဲ (က)</div>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 pt-2 text-center">
           <div className="flex items-center gap-2 text-xs font-bold">
            <input
              id="lawOffice"
              type="text"
              value={formData.lawOffice}
              onChange={(e) => setFormData({ ...formData, lawOffice: e.target.value })}
              className="w-64 border-b border-black bg-transparent px-1 text-center font-normal focus:outline-none"
            />
          </div>
          <h1 className="text-sm md:text-base font-bold leading-normal">
            ဥပဒေရုံး၏ နစ်နာသူ (သို့မဟုတ်) မျက်မြင်သက်သေအား တရားခွင်အကြို တွေ့ဆုံမေးမြန်းသည့် နေ့ရက်များ
          </h1>
        </div>
        {/* <div className="flex justify-between items-start text-xs font-bold">
          <div>
            <div className="flex items-center gap-2">
              <span>မှတ်တမ်းခေါင်းစဉ်/ရုံး -</span>
              <input
                type="text"
                value={formData.officeName}
                onChange={(e) => setFormData({ ...formData, officeName: e.target.value })}
                placeholder="ဖြည့်သွင်းရန်"
                className="border-b border-black bg-transparent focus:outline-none w-64 px-1 text-sm"
              />
            </div>
          </div>
          <div className="text-center space-y-1">
            <div className="border border-black px-3 py-1 text-[11px]">PTI ပုံစံ</div>
            <div className="text-[11px] text-gray-500 font-normal pt-1">အမှုမှတ်တမ်းများဖြည့်သွင်းရန်ပုံစံ</div>
          </div>
        </div> */}

        {/* Dynamic Data Table */}
        <div className="w-full overflow-x-auto border border-black mt-6">
          <table className="w-full text-center border-collapse text-xs min-w-[850px]">
            <thead>
              <tr className="bg-slate-50 border-b border-black font-bold">
                <th className="border-r border-black p-2 w-12">စဉ်</th>
                <th className="border-r border-black p-2 w-28">ရာဇဝတ်ကြီးမှု<br/>အမှတ်</th>
                <th className="border-r border-black p-2 w-24">တရားလို</th>
                <th className="border-r border-black p-2 w-24">တရားခံ</th>
                <th className="border-r border-black p-2 w-32">တရားစွဲဆိုသည့်<br/>ဥပဒေ၊ ပုဒ်မ</th>
                <th className="border-r border-black p-2 w-32">နစ်နာသူ /<br/>မျက်မြင်သက်သေ</th>
                <th className="border-r border-black p-2 w-36">တွေ့ဆုံဆွေးနွေးသည့်<br/>ဥပဒေအရာရှိအမည်</th>
                <th className="border-r border-black p-2 w-28">တွေ့ဆုံသည့်<br/>နေ့ရက်</th>
                <th className="border-r border-black p-2 w-28">မှတ်ချက်</th>
                <th className="p-2 w-16 font-sans text-gray-400 font-normal">Action</th>
              </tr>
              <tr className="bg-slate-50 border-b border-black text-[10px] font-normal">
                {['(၁)', '(၂)', '(၃)', '(၄)', '(၅)', '(၆)', '(၇)', '(၈)', '(၉)'].map((number) => (
                  <th key={number} className="border-r border-black px-2 py-1">{number}</th>
                ))}
                <th className="p-1" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {formData.rows.map((row, index) => (
                <tr key={row.id} className="border-b border-gray-400 hover:bg-slate-50 align-middle">
                  {/* (၁) စဉ် */}
                  <td className="border-r border-black p-2 font-bold text-center">{index + 1}</td>
                  
                  {/* (၂) ရာဇဝတ်ကြီးမှုအမှတ် */}
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.caseNo} onChange={(e) => handleRowChange(row.id, 'caseNo', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* (၃) တရားလို */}
                  <td className="border-r border-black p-1 px-2">
                    <input type="text" value={row.plaintiff} onChange={(e) => handleRowChange(row.id, 'plaintiff', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* (၄) တရားခံ */}
                  <td className="border-r border-black p-1 px-2">
                    <input type="text" value={row.defendant} onChange={(e) => handleRowChange(row.id, 'defendant', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* (၅) တရားစွဲဆိုသည့် ဥပဒေ၊ ပုဒ်မ */}
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.lawSection} onChange={(e) => handleRowChange(row.id, 'lawSection', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* (၆) နစ်နာသူ / မျက်မြင်သက်သေ */}
                  <td className="border-r border-black p-1 px-2">
                    <input type="text" value={row.witnessName} onChange={(e) => handleRowChange(row.id, 'witnessName', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* (၇) တွေ့ဆုံဆွေးနွေးသည့် ဥပဒေအရာရှိအမည် */}
                  <td className="border-r border-black p-1 px-2">
                    <input type="text" value={row.officerName} onChange={(e) => handleRowChange(row.id, 'officerName', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" />
                  </td>

                  {/* (၈) တွေ့ဆုံသည့် နေ့ရက် */}
                  <td className="border-r border-black p-1">
                    <input type="text" value={row.interviewDate} onChange={(e) => handleRowChange(row.id, 'interviewDate', e.target.value)} className="w-full bg-transparent text-center focus:outline-none" placeholder="ရက်-လ-ခုနှစ်" />
                  </td>

                  {/* (၉) မှတ်ချက် */}
                  <td className="border-r border-black p-1 px-2">
                    <input type="text" value={row.remarks} onChange={(e) => handleRowChange(row.id, 'remarks', e.target.value)} className="w-full bg-transparent text-left focus:outline-none" />
                  </td>

                  {/* Action Delete */}
                  <td className="p-1 text-center font-sans">
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(row.id)}
                      className="text-red-600 hover:text-red-800 font-medium text-xs focus:outline-none"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      


        {/* Add Row Button */}
        {!id && (
        <div className="flex justify-start font-sans">
          <button
            type="button"
            onClick={handleAddRow}
            className="bg-slate-800 hover:bg-slate-950 text-white text-xs font-medium px-4 py-2 rounded shadow transition flex items-center gap-1"
          >
            ➕ အတန်းအသစ်ထည့်ရန် (Add New Row)
          </button>
        </div>

        
      )}

      {/* Bottom Signature Area */}
        <div className="flex flex-col items-end pt-8 font-sans">
          <div className="w-64 text-center space-y-2">
            
            {/* Name Input */}
            <div className="flex items-center text-xs justify-center pt-1">
              <span className="font-serif">ဥပဒေအရာရှိအမည် -</span>
              <input 
                type="text" 
                value={formData.responsibleOfficerName} 
                onChange={(e) => setFormData({ ...formData, responsibleOfficerName: e.target.value })}
                placeholder="" 
                className="w-32 bg-transparent border-b border-dotted border-black text-center focus:outline-none font-serif font-bold ml-1" 
              />
            </div>

            <div className="text-xs font-bold font-serif pt-1">ဥပဒေအရာရှိ</div>
          </div>
        </div>

        <div className="text-left text-[11px] font-bold text-gray-600 pt-4">
          * PTI အမှုမှတ်တမ်း
        </div>
      </div>

      {/* Save Button */}
      <div className="w-full max-w-[1400px] mt-4 flex justify-end font-sans">
        <button
          type="button"
          onClick={handleSaveForm}
          disabled={isSaving}
          className={`bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-6 py-2.5 rounded shadow-md transition flex items-center gap-2 ${
            isSaving ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isSaving ? 'သိမ်းဆည်းနေပါသည်...' : id ? '💾 ပြင်ဆင်ချက်များ သိမ်းဆည်းမည်' : '💾 ဒေတာများအားလုံးသိမ်းဆည်းမည်'}
        </button>
      </div>

    </div>
  );
}